<script setup lang="ts">
import Product from '~/models/Product/Product'
import ProductVisualTag from '~/models/Product/ProductVisualTag'
import BadgeSquare from '~/components/elements/BadgeSquare.vue'
import LastItemsOnStock from '~/components/ui/LastItemsOnStock.vue'

const props = defineProps<{ product: Product }>()

const showDiscountOrPricePerPieceBadge = computed(() => {
    return props.product.isAvailable && (props.product.percentDiscount || props.product.pricePerPiece)
})

const resolveMultiActionBadges = computed(() => {
    const visualTags = props.product.visualTags.merged
    const percentageDiscountVisualTags = visualTags.filter((visualTag: ProductVisualTag) => {
        return visualTag.size === 'xl'
    })

    let resultVisualTags: ProductVisualTag[] = []
    if (percentageDiscountVisualTags.length > 0) {
        resultVisualTags = percentageDiscountVisualTags
    } else {
        resultVisualTags = visualTags.filter((visualTag: ProductVisualTag) => visualTag.showInList)
    }

    return resultVisualTags
        .filter((visualTag: ProductVisualTag) => {
            if (showDiscountOrPricePerPieceBadge.value) {
                return true
            }

            return visualTag.sort > 0
        })
        .slice(0, 2)
})

const topVisualTags = computed(() => {
    if (!props.product.visualTags || !props.product.visualTags.merged.length) {
        return []
    }

    return props.product.visualTags.merged.filter((visualTag: ProductVisualTag) => visualTag.sort <= 0)
})

const topGiftEvent = computed(() => {
    return props.product.giftActions?.length ? props.product.giftActions.slice(0, 1)[0] : null
})
</script>

<template>
    <div v-if="product.productCodeSale && product.productCodeSale.hasCodeSale" class="product-badges">
        <div v-if="product.variantsCount > 1" class="item--priceWithCode">
            <span
                v-html="
                    $t('productDetail.priceWithCode.withCodeMasterProduct', {
                        price: $formatCurrency(product.productCodeSale.salePrice),
                    })
                "
            ></span>
            <br />
            <span>{{ product.productCodeSale.voucherCode }}</span>
        </div>
        <div v-else class="item--priceWithCode">
            <span
                v-html="
                    $t('productDetail.priceWithCode.withCode', {
                        price: $formatCurrency(product.productCodeSale.salePrice),
                    })
                "
            ></span>
            <br />
            <span>{{ product.productCodeSale.voucherCode }}</span>
        </div>
    </div>
    <div v-else-if="topGiftEvent && topGiftEvent.priceWithDiscount" class="product-badges">
        <div class="item--priceWithCode">
            <span v-html="$formatCurrency(topGiftEvent.priceWithDiscount)"></span>&nbsp;
            <span v-html="$t('productDetail.giftEvent.withDiscount')"></span>
        </div>
    </div>
    <div v-else class="product-badges">
        <div
            v-for="(badge, index) in resolveMultiActionBadges"
            :key="index"
            class="item"
            :class="`item--${badge.size}`"
            :style="{ 'background-color': badge.backgroundColor, color: badge.textColor }"
        >
            {{ badge.name }}
        </div>

        <div v-if="product.isAvailable && product.hasSuperPrice" class="item--superPrice">
            {{ $t('productDetail.superPrice').toUpperCase() }}
        </div>
        <div v-else-if="product.isAvailable && product.hasCompetitorSuperPrice" class="item--superPrice">
            {{ $t('productDetail.competitorPrice').toUpperCase() }}
        </div>
        <LastItemsOnStock v-else-if="product.lastItemsOnStockMessage" :message="product.lastItemsOnStockMessage" />
        <!--        <div v-else-if="product.cashbackLabel" class="item&#45;&#45;cashback" v-html="product.cashbackLabel"></div>-->
    </div>

    <div v-if="showDiscountOrPricePerPieceBadge" class="product-discount">
        <BadgeSquare v-if="product.pricePerPiece" :price-per-piece="product.pricePerPiece" />
        <div v-else-if="product.percentDiscount" class="discount" data-cy="discount">-{{ product.percentDiscount }} %</div>
    </div>
    <div v-else-if="topVisualTags.length" class="product-badges product-badges--top">
        <div
            v-for="(badge, index) in topVisualTags"
            :key="index"
            class="item"
            :class="`item--${badge.size}`"
            :style="{ 'background-color': badge.backgroundColor, color: badge.textColor }"
        >
            {{ badge.name }}
        </div>
    </div>
</template>

<style scoped lang="scss">
.product-badges {
    flex-direction: column;
    align-items: center;
    justify-content: center;
    color: $color-dark;
    position: absolute;
    bottom: -23px;
    left: 0;
    right: 0;
    padding: 0 8px;
    max-width: 100%;
    display: flex;

    @include screen-lg {
        bottom: -20px;
    }

    &--top {
        top: 0;
        bottom: 0;
        align-items: end;
        justify-content: start;
    }

    &--promo {
        bottom: -40px;
        padding-left: 0;
        align-items: flex-start;

        @include screen-sm {
            bottom: -20px;
        }
    }

    .item {
        font-size: 12px;
        line-height: 14px;
        background-color: rgba(255, 255, 255, 0.9);
        border-radius: 25px;
        padding: 3px 10px;
        margin-bottom: 5px;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        margin-right: 0;
        width: auto;
        font-weight: 500;
        gap: 5px;

        &--superPrice {
            font-size: 14px;
            line-height: 16px;
            background-color: $color-gold;
            border-radius: 10px;
            font-weight: 600;
            padding: 3px 20px;

            @media (max-width: 480px) {
                font-size: 10px;
                line-height: 14px;
            }
        }

        &--cashback {
            color: $color-dark;
            font-size: 12px;
            line-height: 16px;
            background-color: $color-purple-light;
            border-radius: 10px;
            font-weight: 600;
            padding: 5px 10px;
            opacity: 0.9;
        }

        &--priceWithCode {
            color: $color-dark;
            font-size: 14px;
            line-height: 19px;
            background-color: $color-cta-extra-light;
            border-radius: 10px;
            font-weight: 600;
            padding: 5px 10px;
            opacity: 0.9;
        }

        &--xl {
            font-size: 14px;
            line-height: 16px;
            padding: 5px 12px;
        }

        &--sm {
            text-wrap: wrap;
        }
    }
}

.product-discount {
    position: absolute;
    right: 7px;
    top: 0;
    font-size: 13px;
    color: $color-dark;
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    .discount {
        margin-bottom: 0.5rem;
    }
}
</style>
